import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import BarcodeSettingsForm from "./BarcodeSettingsForm";
import "./style.css";
import RollLabelPreview from "./RollLabelPreview";
import SheetLabelPreview from "./SheetLabelPreview";
import { useReactToPrint } from "react-to-print";
import {
  Accordion,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Col,
  Form,
  FormCheck,
  FormGroup,
  Modal,
  Row,
  Container,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
  Spinner
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllBarcodeSettings,
  saveBarcodeSetting,
} from "../../redux/features/barcodeSettings.features";
import { toast } from "react-toastify";
import { Field, FieldArray, Formik } from "formik";
import apiService from "../../services/apiServices";
import { truncateText, formatCurrency } from "../../utility";
import ItemAccordion from "./ItemAccordion";
import { BiBarcode, BiPlus, BiTrash, BiCheckDouble, BiPrinter, BiCog, BiRightArrowAlt, BiLeftArrowAlt, BiCheckCircle } from "react-icons/bi";
import { BsBox, BsGearFill, BsPrinter, BsUpcScan } from "react-icons/bs";
import { FaEdit, FaBarcode, FaQrcode } from "react-icons/fa";
import { fetchCompanies } from "../../redux/features/company.features";
import BrandModal from "../core/components/Brand/BrandModal";

// --- THEME ---
const BRAND_COLOR = "#001c3a";

// --- STEPPER COMPONENT ---
const WizardStep = ({ step, currentStep, icon, title, isLast }) => {
  const isActive = step === currentStep;
  const isCompleted = step < currentStep;

  return (
    <div className={`d-flex align-items-center ${!isLast ? 'flex-grow-1' : ''}`}>
      <div className={`d-flex align-items-center justify-content-center rounded-circle border border-2 transition-all ${isActive ? 'bg-primary text-white border-primary shadow-sm' : isCompleted ? 'bg-success text-white border-success' : 'bg-light text-muted border-light'}`}
        style={{ width: '40px', height: '40px', minWidth: '40px' }}
      >
        {isCompleted ? <BiCheckCircle size={24} /> : icon}
      </div>
      <div className={`ms-2 d-none d-md-block ${isActive ? 'fw-bold text-dark' : 'text-muted'}`}>
        {title}
      </div>
      {!isLast && (
        <div className="flex-grow-1 mx-3" style={{ height: '2px', backgroundColor: isCompleted ? '#198754' : '#e9ecef' }}></div>
      )}
    </div>
  );
};

const AutoSelectHelper = ({ items, setFieldValue, printItems }) => {
  const location = useLocation();
  const hasAutoSelectedRef = useRef(false);

  useEffect(() => {
    // Only auto-select ONCE when coming from an explicit action with autoSelect intent
    if (hasAutoSelectedRef.current) return;

    const isFromAutoSource = Boolean(
      location.state?.autoSelect ||
      location.state?.from === "purchase" ||
      location.state?.from === "stock"
    );

    if (isFromAutoSource && items && items.length > 0) {
      hasAutoSelectedRef.current = true;

      const all = items.map(item => {
        let uniqueId = item.uId;
        if (!uniqueId) {
          uniqueId = item._id;
          if (item.variant_id && !String(item._id).includes(String(item.variant_id))) {
            uniqueId = `${item._id}_${item.variant_id}`;
          }
        }

        const b2bVal = item.b2bPrice || (item.b2bPricing && item.b2bPricing[0]?.price) || item.lastWholesalePrice;
        const displayName = item.variantName 
            ? (item.variantName.toLowerCase().includes(item.itemName?.toLowerCase()) 
                ? item.variantName 
                : `${item.itemName} - ${item.variantName}`)
            : item.itemName;

        const isSerialized = item.serialized ?? item.isSerialized ?? false;

        return {
          serialized: isSerialized,
          serializedItems: item.serializedItems || [],
          itemName: displayName,
          baseItemName: item.itemName,
          variantName: item.variantName,
          variantAttributes: item.variantAttributes || [],
          _id: uniqueId,
          uId: uniqueId,
          barcode: item.barcode,
          lastSellingPrice: item.lastSellingPrice,
          b2bPrice: b2bVal,
          b2bPricing: item.b2bPricing || (b2bVal ? [{ minQty: 1, price: b2bVal }] : []),
          totalStock: item.totalStock,
          category: item.category,
          batchNumbers: item.batchNumbers || [...new Set((item.batches || []).map((x) => x.batch_number))],
          batches: item.batches || [],
          // SAFE DEFAULT: 1 label per batch by default (prevents runaway 1000+ prints)
          batchData: (!isSerialized && (!item.serializedItems || item.serializedItems.length === 0)) ? (item.batches || []).reduce((acc, b) => {
            const batchB2b = b.b2bPrice || (b.b2bPricing && b.b2bPricing[0]?.price) || b2bVal;
            const batchDisplayName = b.itemName || displayName;
            acc[b.batch_number] = {
              qty: 1, // 1 label per batch by default
              sellingPrice: b.sellingPrice || item.lastSellingPrice,
              b2bPrice: batchB2b,
              b2bPricing: b.b2bPricing || (batchB2b ? [{ minQty: 1, price: batchB2b }] : []),
              barcode: b.barcode || item.barcode,
              itemName: batchDisplayName
            };
            return acc;
          }, {}) : {}
        };
      });

      setFieldValue("printItems", all);
    }
  }, [items, location.state, setFieldValue]);

  return null;
};
const AutoSelectHelper_legacy = ({ items, setFieldValue, printItems }) => {
  const location = useLocation();
  useEffect(() => {
    const isFromAutoSource = location.state?.autoSelect || location.state?.from === "purchase" || location.state?.from === "stock";
    if (isFromAutoSource && items?.length > 0 && printItems?.length === 0) {
      const all = items.map(item => {
        // item already comes enriched from PurchaseList (e.g. itemName is already the full variant name)
        return {
          serialized: item.serialized,
          serializedItems: item.serializedItems || [],
          itemName: item.itemName,
          _id: item._id, // This is already unique (itemID_variantID)
          barcode: item.barcode,
          lastSellingPrice: item.lastSellingPrice,
          totalStock: item.totalStock,
          category: item.category,
          batchNumbers: [...new Set((item.batches || []).map((x) => x.batch_number))],
        };
      });
      setFieldValue("printItems", all);
    }
  }, [items, location.state, setFieldValue, printItems.length]);
  return null;
};

const PrintableLabels = ({ items }) => {
  const [barcodeUrls, setBarcodeUrls] = useState([]);
  const [labelsettings, Setlabelsettings] = useState({});
  const [loading, setLoading] = useState(true);
  const printRef = useRef();
  const [pageStyle, setPageStyle] = useState("");

  // V2: react-to-print (industry standard — prints live DOM, no clone)
  const handlePrint_v2 = useReactToPrint({
    contentRef: printRef,
    pageStyle: () => `
      @page {
        size: ${getPageSize(labelsettings)};
        margin: 0mm;
      }
      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: white !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .roll-container {
          display: block !important;
          margin: 0 !important;
          padding: ${labelsettings.padding || 1}mm !important;
        }
        .label {
          width: ${labelsettings.labelWidth || 38}mm !important;
          height: ${labelsettings.labelHeight || 25}mm !important;
          max-width: ${labelsettings.labelWidth || 38}mm !important;
          max-height: ${labelsettings.labelHeight || 25}mm !important;
          margin: 0 !important;
          padding: 0.8mm 1.2mm !important;
          box-sizing: border-box !important;
          border: none !important;
          page-break-after: always !important;
          page-break-inside: avoid !important;
          break-after: page !important;
          break-inside: avoid !important;
        }
        .label:last-child {
          page-break-after: avoid !important;
          break-after: avoid !important;
        }
      }
    `,
  });
  const dispatch = useDispatch();

  // UI State for Wizard
  const [currentStep, setCurrentStep] = useState(1);
  const [showBarcodeForm, setShowBarcodeForm] = useState(false);

  // UI State for Filter & Sort
  const [filterCategory, setFilterCategory] = useState("All");
  const [sortType, setSortType] = useState("name_asc");
  const [printB2B, setPrintB2B] = useState(false);

  const location = useLocation();

  const barcodeSettings = useSelector(
    (state) => state.barcodeSettingsReducer.settings
  );
  useEffect(() => {
    dispatch(getAllBarcodeSettings());
    dispatch(fetchCompanies());
  }, [dispatch]);

  const resolveLabelItemName = (entity, parent) => {
    let vName = entity?.variantName || parent?.variantName || "";
    let bName = parent?.baseItemName || parent?.itemName || entity?.baseItemName || entity?.itemName || "";

    if (vName) {
      if (bName && !vName.toLowerCase().includes(bName.toLowerCase())) {
        return `${bName} - ${vName}`;
      }
      return vName;
    }
    return entity?.itemName || parent?.itemName || "Unknown Item";
  };

  const resolveLabelPrice = (entity, parent, isB2BMode) => {
    if (isB2BMode) {
      const b2b = entity?.b2bPrice ??
                  (entity?.b2bPricing && entity?.b2bPricing[0]?.price) ??
                  parent?.b2bPrice ??
                  (parent?.b2bPricing && parent?.b2bPricing[0]?.price) ??
                  parent?.lastWholesalePrice;
      if (b2b !== undefined && b2b !== null && !isNaN(Number(b2b)) && Number(b2b) > 0) {
        return Number(b2b);
      }
    }
    const retail = entity?.sellingPrice ?? entity?.lastSellingPrice ?? parent?.sellingPrice ?? parent?.lastSellingPrice ?? 0;
    return Number(retail) || 0;
  };

  const generateBarcodes = (
    settings = {
      bcid: "code128",
      text: "",
      scale: 3,
      height: 20,
      includetext: true,
      textxalign: "center",
      textsize: 12,
      backgroundcolor: "ffffff",
      paddingtop: 5,
      paddingbottom: 5,
      textyoffset: 3,
      itemNameFontSize: 12,
      companyNameFontSize: 12,
      priceFontSize: 12,
      itemNameX: "50%",
      itemNameY: "10",
      companyNameX: "90%",
      companyNameY: "100%",
      priceX: "5%",
      priceY: "100%",
      deviceMode: 'both', // 'both', 'price_only', 'battery_health_only'
    },
    isB2B = printB2B
  ) => {
    try {
      const urls = [];
      (settings.printItems || []).forEach((itm) => {
        const itemDisplayName = resolveLabelItemName(itm, itm);

        if (itm.serialized) {
          for (const serial of itm.serializedItems || []) {
            const displayName = resolveLabelItemName(serial, itm) || itemDisplayName;
            const price = resolveLabelPrice(serial, itm, isB2B);
            const qty = Number(serial.availableQty) > 1 ? Number(serial.availableQty) : 1;

            for (let k = 0; k < qty; k++) {
              urls.push({
                itemName: displayName,
                barcode: serial.serialNumber,
                sellingPrice: price,
                batteryHealth: serial?.batteryHealth,
                condition: serial?.condition,
                category: serial.category || itm.category,
                showPrice: settings.showPrice !== false,
                deviceMode: settings.deviceMode || 'both',
                showBatteryHealth: settings.showBatteryHealth !== false,
              });
            }
          }
        } else {
          // Non-serialized: use batchData if available (batch-specific qty and price)
          if (itm.batchData && Object.keys(itm.batchData).length > 0) {
            // Generate labels for each batch with its specific price
            Object.entries(itm.batchData).forEach(([batchNumber, batchInfo]) => {
              const displayName = resolveLabelItemName(batchInfo, itm) || itemDisplayName;
              const price = resolveLabelPrice(batchInfo, itm, isB2B);
              const qty = Number(batchInfo.qty) || 0;

              for (let i = 0; i < qty; i++) {
                urls.push({
                  itemName: displayName,
                  barcode: batchInfo.barcode || itm.barcode,
                  sellingPrice: price,
                  category: batchInfo.category || itm.category,
                  showPrice: settings.showPrice !== false,
                  showBatteryHealth: settings.showBatteryHealth !== false,
                  deviceMode: settings.deviceMode || 'both',
                });
              }
            });
          } else {
            // Fallback: use 1 label as default instead of totalStock to prevent massive print jobs
            const price = resolveLabelPrice(itm, itm, isB2B);
            const qty = 1; // Changed from Number(itm.totalStock) || 0

            for (let i = 0; i < qty; i++) {
              urls.push({
                itemName: itemDisplayName,
                barcode: itm.barcode, // itm.barcode is already variant-aware from PurchaseList
                sellingPrice: price,
                category: itm.category,
                showPrice: settings.showPrice !== false,
                showBatteryHealth: settings.showBatteryHealth !== false,
                deviceMode: settings.deviceMode || 'both',
              });
            }
          }
        }
      });

      setBarcodeUrls(urls);
      setLoading(false);
    } catch (error) {
      console.error("Error generating barcodes:", error);
      setLoading(false);
    }
  };

  function getPageSize(_labelsettings) {
    if (_labelsettings.paperType === "roll") {
      return `${_labelsettings.labelWidth || 38}mm ${_labelsettings.labelHeight || 25}mm landscape`;
    } else if (_labelsettings.pageSize === "custom") {
      return `${_labelsettings.labelWidth || 38}mm ${_labelsettings.labelHeight || 25}mm landscape`;
    } else {
      return _labelsettings.pageSize || "auto";
    }
  }

  const handleSubmit = (submitValue) => {
    setLoading(true);
    Setlabelsettings(submitValue);
    generateBarcodes(submitValue);
    // If coming from wizard, this is usually final step logic, but we call it dynamically
  };

  const {
    labelWidth = 38, // mm
    labelHeight = 25, // mm
    padding = 1, // mm
    gap = 3, // mm
    gridColumns = 1, // Labels per row
    pageWidth = 210, // A4 width in mm
    pageHeight = 297, // A4 height in mm
    orientation = "portrait", // 'portrait' or 'landscape'
  } = labelsettings;

  const mmToPx = (mm) => Math.round(mm * 8); // 1mm ≈ 8px at 203 DPI

  const containerStyle = {
    display: "grid",
    gridTemplateColumns: `repeat(${gridColumns}, ${mmToPx(labelWidth)}px)`,
    gap: `${mmToPx(gap)}px`,
    justifyContent: "center",
    background: "white",
    width: `${mmToPx(pageWidth)}px`,
    padding: `${mmToPx(padding)}px`,
    transform: orientation === "landscape" ? "rotate(90deg)" : "none",
  };

  const labelStyle = {
    width: `${mmToPx(labelWidth)}px`,
    height: `${mmToPx(labelHeight)}px`,
    display: "flex",
    background: "white",
    padding: 0,
    boxSizing: "border-box",
  };

  const handlePrintWindow = () => {
    const printWindow = window.open(
      "",
      "_blank",
      `width=${window.screen.width},height=${window.screen.height},top=0,left=0`
    );

    if (!printWindow) {
      alert("Popup blocked. Please allow popups and try again.");
      return;
    }
    const styleTag = document.createElement("style");
    styleTag.innerHTML = ` @page {
      size: ${getPageSize(labelsettings)};
      margin: 0mm;
    }
    
    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: white !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
    
      .roll-container {
        display: block !important;
        margin: 0 !important;
        padding: 0 !important;
      }
    
      .label {
        width: ${labelsettings.labelWidth || 38}mm !important;
        height: ${labelsettings.labelHeight || 25}mm !important;
        max-width: ${labelsettings.labelWidth || 38}mm !important;
        max-height: ${labelsettings.labelHeight || 25}mm !important;
        margin: 0 !important;
        padding: 0.8mm 1.2mm !important;
        box-sizing: border-box !important;
        border: none !important;
        page-break-after: always !important;
        page-break-inside: avoid !important;
        break-after: page !important;
        break-inside: avoid !important;
      }

      .label:last-child {
        page-break-after: avoid !important;
        break-after: avoid !important;
      }
    
      .labelHeader {
        color: #000;
        width: 100%;
        padding-top: 1px;
        margin-top: 0px;
        line-height: 1.1;
        font-weight: 800;
        display: -webkit-box;
        -webkit-line-clamp: 2; 
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        font-family: inherit;
      }
    
      .labelText {
        font-family: inherit;
      }
    
      .sheet-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        page-break-before: always;
      }
    
      .sheet-container .label {
        width: ${labelsettings.labelWidth}mm;
        height: ${labelsettings.labelHeight}mm;
        margin-bottom: ${labelsettings.gap}mm;
        padding: ${labelsettings.padding}mm !important;
      }
    }
    `;

    printWindow.document.head.appendChild(styleTag);

    const contentContainer = printWindow.document.createElement("div");
    contentContainer.style = { ...contentContainer.style, ...containerStyle };

    const clonedContent = printRef.current.cloneNode(true);
    contentContainer.appendChild(clonedContent);
    printWindow.document.body.appendChild(contentContainer);

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 1000);

    printWindow.document.close();
  };

  return (
    <div className="container-fluid p-4" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', minHeight: '100vh' }}>
      {/* WIZARD HEADER */}
      <div className="bg-white p-3 rounded-4 shadow-sm mb-4 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-4 w-75">
          <WizardStep step={1} currentStep={currentStep} icon={<BiCog size={20} />} title="Configuration" />
          <WizardStep step={2} currentStep={currentStep} icon={<BiCheckDouble size={20} />} title="Select Items" />
          <WizardStep step={3} currentStep={currentStep} icon={<BiPrinter size={20} />} title="Preview & Print" isLast />
        </div>
        <div>
          <h5 className="fw-bold mb-0 text-end" style={{ color: BRAND_COLOR }}>Label Studio</h5>
          <div className="x-small text-muted text-end">Step {currentStep} of 3</div>
        </div>
      </div>



      <Formik
        initialValues={{ printItems: [], barcodeSettingId: "" }}
        onSubmit={() => { }}
      >
        {({ values, setFieldValue }) => (
          <>
            <AutoSelectHelper items={items} setFieldValue={setFieldValue} printItems={values.printItems} />

            <div className="row g-4 h-100">
              {/* --- STEP 1: CONFIGURATION --- */}
              {currentStep === 1 && (
                <Col md={12} className="animate__animated animate__fadeIn">
                  <Card className="border-0 shadow-sm p-4 text-center" style={{ minHeight: '60vh' }}>
                    <div className="mb-4">
                      <div className="p-3 bg-primary bg-opacity-10 rounded-circle d-inline-block mb-3">
                        <BiCog size={40} className="text-primary" />
                      </div>
                      <h4>Choose Your Print Template</h4>
                      <p className="text-muted">Select a predefined configuration or create a new one.</p>
                    </div>
                    <div className="d-flex justify-content-center gap-3 mb-5">
                      <div style={{ maxWidth: '400px', width: '100%' }}>
                        <Form.Select
                          size="lg"
                          className="shadow-sm border-primary text-center fw-bold"
                          value={values.barcodeSettingId}
                          onChange={e => setFieldValue('barcodeSettingId', e.target.value)}
                        >
                          <option value="">-- Select Template --</option>
                          {barcodeSettings.map((setting) => (
                            <option key={setting._id} value={setting._id}>
                              {setting.settingName}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                    </div>
                    <div className="d-flex justify-content-center gap-3">
                      <Button variant="outline-primary" onClick={() => { setFieldValue('barcodeSettingId', ''); setShowBarcodeForm(true); }}>
                        <BiPlus /> Create New
                      </Button>
                      <Button variant="primary" disabled={!values.barcodeSettingId} onClick={() => setCurrentStep(2)} className="px-5 fw-bold shadow">
                        Next Step <BiRightArrowAlt />
                      </Button>
                    </div>
                    {values.barcodeSettingId && (
                      <div className="mt-3">
                        <Button variant="link" size="sm" onClick={() => setShowBarcodeForm(true)}>Edit Selected Template</Button>
                      </div>
                    )}
                  </Card>
                </Col>
              )}

              {/* --- STEP 2: SELECTION --- */}
              {currentStep === 2 && (() => {
                let totalLabels = 0;
                (values.printItems || []).forEach(itm => {
                  if (itm.serialized) {
                    totalLabels += (itm.serializedItems?.length || 0);
                  } else {
                    if (itm.batchData && Object.keys(itm.batchData).length > 0) {
                      Object.values(itm.batchData).forEach(b => {
                        totalLabels += Number(b.qty) || 0;
                      });
                    } else {
                      totalLabels += (itm.batchNumbers?.length || 1);
                    }
                  }
                });
                const selectedProductsCount = (values.printItems || []).length;

                return (
                  <Col md={12} className="animate__animated animate__fadeIn">
                    {/* HIGH-FIDELITY LIVE STATUS BAR */}
                    <div className="bg-white p-3 rounded-4 shadow-sm mb-3 border d-flex flex-wrap justify-content-between align-items-center gap-3">
                      <Button variant="light" className="rounded-pill px-3 fw-semibold border shadow-xs d-flex align-items-center gap-1" onClick={() => setCurrentStep(1)}>
                        <BiLeftArrowAlt size={18} /> Back to Template
                      </Button>
                      
                      <div className="d-flex align-items-center gap-3">
                        <h5 className="mb-0 fw-bold text-dark d-none d-md-block">Select Items to Print</h5>
                        <div className="d-flex align-items-center gap-2">
                          <span className={`badge px-3 py-2 rounded-pill font-monospace fw-bold ${selectedProductsCount > 0 ? 'bg-primary text-white shadow-sm' : 'bg-light text-muted border'}`} style={{ fontSize: '0.85rem' }}>
                            <BsBox className="me-1" />
                            {selectedProductsCount} {selectedProductsCount === 1 ? 'Product' : 'Products'} Selected
                          </span>
                          <span className={`badge px-3 py-2 rounded-pill font-monospace fw-bold ${totalLabels > 0 ? 'bg-success text-white shadow-sm' : 'bg-light text-muted border'}`} style={{ fontSize: '0.85rem' }}>
                            <BiPrinter className="me-1" />
                            {totalLabels} {totalLabels === 1 ? 'Label' : 'Labels'} Total
                          </span>
                        </div>
                      </div>

                      <Button
                        variant={totalLabels > 0 ? "primary" : "secondary"}
                        disabled={values.printItems.length === 0}
                        onClick={() => {
                          const selectedSetting = barcodeSettings.find(s => s._id === values.barcodeSettingId);
                          if (selectedSetting) {
                            handleSubmit({ ...selectedSetting, printItems: values.printItems });
                            setCurrentStep(3);
                          }
                        }}
                        className="px-4 py-2 fw-bold rounded-pill shadow-sm d-flex align-items-center gap-2"
                      >
                        <span>Generate Preview</span>
                        {totalLabels > 0 && (
                          <span className="badge bg-white text-primary rounded-pill px-2">{totalLabels}</span>
                        )}
                        <BiRightArrowAlt size={18} />
                      </Button>
                    </div>

                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                      <ItemAccordion items={items} />
                    </Card>

                    {/* FLOATING STICKY ACTION DOCK (For effortless scrolling workflow) */}
                    {selectedProductsCount > 0 && (
                      <div
                        className="position-fixed bottom-0 start-50 translate-middle-x mb-4 p-2 px-4 rounded-pill shadow-lg d-flex align-items-center gap-4 animate__animated animate__slideInUp"
                        style={{ zIndex: 1050, backdropFilter: 'blur(8px)', background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(255, 255, 255, 0.15)' }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <div className="d-flex align-items-center justify-content-center bg-primary text-white rounded-circle" style={{ width: '32px', height: '32px' }}>
                            <BiCheckDouble size={20} />
                          </div>
                          <div className="small">
                            <span className="fw-bold text-white">{selectedProductsCount} Products</span>
                            <span className="text-secondary mx-2">•</span>
                            <span className="fw-bold text-success font-monospace">{totalLabels} Total Labels</span>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                          <Button
                            variant="outline-light"
                            size="sm"
                            className="rounded-pill px-3 x-small fw-bold border-opacity-50"
                            onClick={() => setFieldValue("printItems", [])}
                          >
                            Clear
                          </Button>
                          <Button
                            variant="success"
                            size="sm"
                            className="rounded-pill px-4 fw-bold shadow-sm d-flex align-items-center gap-1"
                            onClick={() => {
                              const selectedSetting = barcodeSettings.find(s => s._id === values.barcodeSettingId);
                              if (selectedSetting) {
                                handleSubmit({ ...selectedSetting, printItems: values.printItems });
                                setCurrentStep(3);
                              }
                            }}
                          >
                            <span>Preview ({totalLabels})</span>
                            <BiRightArrowAlt size={16} />
                          </Button>
                        </div>
                      </div>
                    )}
                  </Col>
                );
              })()}

              {/* --- STEP 3: PREVIEW --- */}
              {currentStep === 3 && (
                <>
                  <Col lg={8} className="animate__animated animate__fadeIn">
                    <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                      <Button variant="light" size="sm" onClick={() => setCurrentStep(2)}><BiLeftArrowAlt /> Back to Selection</Button>

                      {/* --- ON-THE-FLY BARCODE / QR CODE FORMAT SWITCHER --- */}
                      <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted fw-bold">Format:</span>
                        <div className="btn-group btn-group-sm shadow-sm" role="group">
                          <button
                            type="button"
                            className={`btn fw-bold px-3 ${labelsettings.bcid !== 'qrcode' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => {
                              const updated = { ...labelsettings, bcid: 'code128', printItems: values.printItems };
                              Setlabelsettings(updated);
                              generateBarcodes(updated, printB2B);
                            }}
                          >
                            <FaBarcode className="me-1" /> 1D Barcode
                          </button>
                          <button
                            type="button"
                            className={`btn fw-bold px-3 ${labelsettings.bcid === 'qrcode' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => {
                              const updated = { ...labelsettings, bcid: 'qrcode', printItems: values.printItems };
                              Setlabelsettings(updated);
                              generateBarcodes(updated, printB2B);
                            }}
                          >
                            <FaQrcode className="me-1" /> 2D QR Code + Name
                          </button>
                        </div>
                      </div>

                      {/* --- WHOLESALE (B2B) SWITCH --- */}
                      <div>
                         <Form.Check 
                           type="switch"
                           id="b2b-switch"
                           label={<span className="fw-bold text-primary small">Wholesale (B2B) Price</span>}
                           checked={printB2B}
                           onChange={(e) => {
                             const isChecked = e.target.checked;
                             setPrintB2B(isChecked);
                             if (Object.keys(labelsettings).length > 0) {
                               generateBarcodes(labelsettings, isChecked);
                             }
                           }}
                         />
                      </div>
                    </div>
                    <div className="bg-light rounded shadow-sm overflow-auto" style={{ maxHeight: '70vh', border: '1px dashed #ccc', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '16px' }}>
                      {loading ? <Spinner animation="border" /> : (
                        <div style={{
                          background: 'white',
                          boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                          width: `${labelsettings.labelWidth}mm`,
                          minWidth: `${labelsettings.labelWidth}mm`,
                          overflow: 'hidden',
                        }}>
                          {labelsettings.paperType === "roll" ? (
                            <RollLabelPreview ref={printRef} {...labelsettings} style={labelStyle} labels={barcodeUrls} />
                          ) : (
                            <SheetLabelPreview ref={printRef} {...labelsettings} style={labelStyle} labels={barcodeUrls} />
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col lg={4}>
                    <Card className="border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                      <Card.Body>
                        <h5 className="fw-bold mb-3">Ready to Print</h5>
                        <div className="d-flex justify-content-between mb-2">
                          <span>Total Labels:</span>
                          <span className="fw-bold text-primary">{barcodeUrls.length}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-4">
                          <span>Template:</span>
                          <span className="fw-bold">{labelsettings.settingName}</span>
                        </div>
                        <Button
                          variant="success"
                          size="lg"
                          className="w-100 fw-bold shadow mb-2"
                          onClick={handlePrint_v2}
                          disabled={barcodeUrls.length === 0}
                        >
                          <BiPrinter size={22} className="me-2" /> PRINT New (V2)
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="w-100 mb-3"
                          onClick={handlePrintWindow}
                          disabled={barcodeUrls.length === 0}
                        >
                          <BiPrinter size={16} className="me-1" /> PRINT (V1 — popup window)
                        </Button>
                        <div className="text-center mb-3">
                          <small className="text-muted">Ensure your printer settings match the template dimensions.</small>
                        </div>

                        {/* --- ACTIVE TEMPLATE CONFIGURATION SUMMARY --- */}
                        <div className="p-3 bg-light rounded-3 border text-start">
                          <div className="d-flex align-items-center justify-content-between mb-3">
                            <span className="text-secondary small text-uppercase fw-bold">Template Config</span>
                            <div className="d-flex align-items-center gap-2">
                              <Badge bg="primary" className="fw-normal">{labelsettings.settingName || "Active"}</Badge>
                              <Button 
                                variant="outline-primary" 
                                size="sm" 
                                className="py-0 px-2 x-small fw-bold"
                                onClick={() => setShowBarcodeForm(true)}
                                title="Edit Full Template"
                              >
                                <FaEdit className="me-1" /> Edit
                              </Button>
                            </div>
                          </div>

                          <div className="d-flex justify-content-between py-1 border-bottom small">
                            <span className="text-muted">Dimensions:</span>
                            <span className="fw-bold text-dark">{labelsettings.labelWidth || 38}mm × {labelsettings.labelHeight || 25}mm</span>
                          </div>
                          <div className="d-flex justify-content-between py-1 border-bottom small">
                            <span className="text-muted">Paper Type:</span>
                            <span className="fw-bold text-dark text-capitalize">{labelsettings.paperType || "roll"}</span>
                          </div>
                          <div className="d-flex justify-content-between py-1 border-bottom small">
                            <span className="text-muted">Symbology:</span>
                            <span className="fw-bold text-dark text-uppercase">{labelsettings.bcid || "code128"}</span>
                          </div>

                          {/* Quick On-the-Fly Toggle for Show Price */}
                          <div className="d-flex justify-content-between align-items-center py-1 border-bottom small">
                            <span className="text-muted">Show Price:</span>
                            <Form.Check
                              type="switch"
                              id="quick-show-price"
                              checked={labelsettings.showPrice !== false}
                              onChange={(e) => {
                                const newVal = e.target.checked;
                                const updated = { ...labelsettings, showPrice: newVal, printItems: values.printItems };
                                Setlabelsettings(updated);
                                generateBarcodes(updated, printB2B);

                                const targetId = labelsettings._id || values.barcodeSettingId;
                                if (targetId) {
                                  apiService.put(`/barcode-settings/${targetId}`, { ...labelsettings, showPrice: newVal })
                                    .then(() => {
                                      dispatch(getAllBarcodeSettings());
                                      toast.success(`Price display ${newVal ? 'Enabled' : 'Hidden'} for template`);
                                    })
                                    .catch(err => console.error("Quick toggle error:", err));
                                }
                              }}
                              label={
                                <Badge bg={labelsettings.showPrice !== false ? "success" : "secondary"} className="cursor-pointer">
                                  {labelsettings.showPrice !== false ? "Enabled" : "Hidden"}
                                </Badge>
                              }
                            />
                          </div>

                          {/* Quick On-the-Fly Toggle for Battery Health */}
                          <div className="d-flex justify-content-between align-items-center py-1 border-bottom small">
                            <span className="text-muted">Battery Health:</span>
                            <Form.Check
                              type="switch"
                              id="quick-battery-health"
                              checked={labelsettings.showBatteryHealth !== false}
                              onChange={(e) => {
                                const newVal = e.target.checked;
                                const updated = { ...labelsettings, showBatteryHealth: newVal, printItems: values.printItems };
                                Setlabelsettings(updated);
                                generateBarcodes(updated, printB2B);

                                const targetId = labelsettings._id || values.barcodeSettingId;
                                if (targetId) {
                                  apiService.put(`/barcode-settings/${targetId}`, { ...labelsettings, showBatteryHealth: newVal })
                                    .then(() => {
                                      dispatch(getAllBarcodeSettings());
                                      toast.success(`Battery Health ${newVal ? 'Enabled' : 'Disabled'}`);
                                    })
                                    .catch(err => console.error("Quick toggle error:", err));
                                }
                              }}
                              label={
                                <Badge bg={labelsettings.showBatteryHealth !== false ? "success" : "secondary"} className="cursor-pointer">
                                  {labelsettings.showBatteryHealth !== false ? "Enabled" : "Disabled"}
                                </Badge>
                              }
                            />
                          </div>

                          <div className="d-flex justify-content-between py-1 small">
                            <span className="text-muted">Gap / Padding:</span>
                            <span className="fw-bold text-dark">{labelsettings.gap || 0}mm / {labelsettings.padding || 0}mm</span>
                          </div>

                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="w-100 mt-3 fw-bold d-flex align-items-center justify-content-center gap-2"
                            onClick={() => setShowBarcodeForm(true)}
                          >
                            <FaEdit size={14} /> Edit Full Template Settings
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </>
              )}
            </div>

            {/* SETTINGS MODAL */}
            <BrandModal
              size="fullscreen"
              show={showBarcodeForm}
              onHide={() => setShowBarcodeForm(false)}
              title="Barcode Template Editor"
              subtitle="Configure dimensions and layout"
              icon={<FaEdit />}
            >
              <BarcodeSettingsForm
                items={items}
                initialValues={barcodeSettings.find(s => s._id === values.barcodeSettingId || s._id === labelsettings._id) || labelsettings}
                onSubmit={(e) => {
                  const targetId = e._id || values.barcodeSettingId || labelsettings._id;
                  const action = targetId ? apiService.put(`/barcode-settings/${targetId}`, e) : apiService.post('/barcode-settings', e);
                  action.then((res) => {
                    toast.success("Template Saved Successfully");
                    dispatch(getAllBarcodeSettings());
                    setShowBarcodeForm(false);
                    const savedConfig = res.data || e;
                    const updated = { ...labelsettings, ...savedConfig, printItems: values.printItems };
                    Setlabelsettings(updated);
                    generateBarcodes(updated, printB2B);
                  }).catch(err => toast.error(err.message));
                }}
              />
            </BrandModal>
          </>
        )}
      </Formik>


    </div>
  );
};

export default PrintableLabels;